<script>
  import { onMount, onDestroy } from 'svelte';
  import { editStore, settingsStore } from '../stores';
  import { getRandomDogFact } from './dogFacts.js';
  
  // ─── 강아지 상식 표시 여부 (true: 표시, false: 숨김) ───
  // 이 값을 변경하면 로딩 오버레이에서 강아지 상식 섹션이 보이거나 숨겨집니다.
  const SHOW_DOG_FACTS = true;

  // 대시보드로 이동하는 함수
  function goToDashboard() {
    // 로딩 오버레이 숨기기 
    editStore.update(store => ({
      ...store,
      isLoading: false,
      hideOverlayForCurrentTask: true  // 현재 작업에 대한 오버레이 숨김 플래그
    }));
    
    // 대시보드로 이동
    window.history.pushState({}, '', '/dashboard');
    document.dispatchEvent(new CustomEvent('navigate', { detail: 'dashboard' }));
  }

  let dogFactInterval;
  // gif 이미지를 제거하고 간단한 로딩 메시지만 표시
  const defaultLoadingTips = [
    '이미지 처리 중입니다. 잠시만 기다려주세요.',
    '고품질 결과물을 위해 처리 중입니다.',
    '반려견 이미지를 분석하고 있습니다.',
    '최적의 결과를 위해 AI 모델이 작업 중입니다.',
    '잠시만 기다려주시면 멋진 결과를 보여드릴게요!'
  ];
  const styleLoadingTips = {
    '스크래치 달빛': [
      '달빛 한 조각, 긁어서 꺼내는 중...',
      '별들이 줄을 잘 섰는지 확인하는 중...',
      '밤하늘에 귀여움 한 줄 더 긋는 중...'
    ],
    '유화': [
      '물감 한 스푼, 귀여움 두 스푼 올리는 중...',
      '붓도 감탄할 만큼 닮았는지 보는 중...',
      '마지막 붓질에 사랑스러움을 듬뿍 담는 중...'
    ],
    '색연필': [
      '색연필을 깎고, 귀여움을 뾰족하게 준비하는 중...',
      '볼에 묻은 색연필 가루를 톡톡 터는 중...',
      '종이 위에 사각사각 매력을 채우는 중...'
    ]
  };
  let currentTipIndex = 0;
  let tipInterval;
  $: styleTips = ['style_transform', 'google_style_transform', 'flux_style_transform']
    .includes($editStore.selectedFeature?.id)
    ? styleLoadingTips[$settingsStore.selectedStyle]
    : null;
  $: loadingTips = styleTips || defaultLoadingTips;
  $: currentTip = loadingTips[currentTipIndex % loadingTips.length];
  
  // 랜덤 강아지 사실 사용
  function getNextDogFact() {
    return getRandomDogFact();
  }

  onMount(() => {
    // 강아지 상식이 활성화된 경우에만 관련 인터벌 시작
    if (SHOW_DOG_FACTS) {
      // 초기 강아지 사실 설정 (랜덤)
      editStore.update(store => ({
        ...store,
        dogFact: getRandomDogFact()
      }));
      
      // 10초마다 강아지 사실 변경
      dogFactInterval = setInterval(async () => {
        editStore.update(store => ({
          ...store,
          dogFact: getNextDogFact()
        }));
      }, 10000);
    }
    
    // 5초마다 로딩 팁 변경
    tipInterval = setInterval(() => {
      currentTipIndex = (currentTipIndex + 1) % loadingTips.length;
      editStore.update(store => ({
        ...store,
        loadingText: loadingTips[currentTipIndex]
      }));
    }, 5000);
    
    // 초기 로딩 텍스트 설정
    if (!$editStore.loadingText) {
      editStore.update(store => ({
        ...store,
        loadingText: currentTip
      }));
    }
  });
  
  onDestroy(() => {
    clearInterval(dogFactInterval);
    clearInterval(tipInterval);
  });
</script>

<div class="loading-overlay">
  <div class="loading-content">
    <h3>{styleTips ? currentTip : $editStore.loadingText || currentTip}</h3>
    
    <div class="progress-container">
      <div class="progress-bar">
        <div class="progress-fill"></div>
      </div>
    </div>
    
    {#if SHOW_DOG_FACTS && $editStore.dogFact}
      <div class="dog-fact">
        <h4>🐶 강아지 상식</h4>
        <p class="fact-text">{$editStore.dogFact}</p>
      </div>
    {/if}
    
    <button class="dashboard-button" on:click={goToDashboard}>
      대시보드로 이동
    </button>
  </div>
</div>

<style>
  .loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.85);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
    backdrop-filter: blur(8px);
    animation: fadeIn 0.3s ease;
  }

  :global(.light) .loading-overlay {
    background-color: rgba(255, 255, 255, 0.85);
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .loading-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    width: 560px;
    max-width: 90vw;
    padding: 1.5rem;
    background-color: rgba(26, 26, 26, 0.95);
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    animation: slideUp 0.5s ease;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  :global(.light) .loading-content {
    background-color: rgba(255, 255, 255, 0.95);
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }

  @keyframes slideUp {
    from { transform: translateY(30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }

  h3 {
    margin: 0 0 1.5rem 0;
    font-size: 1.5rem;
    background: linear-gradient(90deg, #FF9A3E, #FF6A00);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: gradientShift 3s ease infinite;
    background-size: 200% auto;
  }

  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  
  .progress-container {
    width: 100%;
    margin-bottom: 1.5rem;
  }
  
  .progress-bar {
    width: 100%;
    height: 6px;
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
    overflow: hidden;
    position: relative;
  }

  :global(.light) .progress-bar {
    background-color: rgba(0, 0, 0, 0.1);
  }
  
  .progress-fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 30%;
    background: linear-gradient(90deg, #FF9A3E, #FF6A00);
    border-radius: 3px;
    animation: progress 2s ease-in-out infinite alternate;
  }
  
  @keyframes progress {
    0% { width: 15%; left: 0; }
    50% { width: 30%; }
    100% { width: 15%; left: 85%; }
  }

  .dog-fact {
    margin-top: 1rem;
    padding: 1.5rem;
    background-color: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    width: 100%;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
  }

  :global(.light) .dog-fact {
    background-color: rgba(0, 0, 0, 0.05);
  }
  
  .dog-fact::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(to bottom, #FF9A3E, #FF6A00);
  }

  .dog-fact:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
    background-color: rgba(255, 255, 255, 0.08);
  }

  :global(.light) .dog-fact:hover {
    background-color: rgba(0, 0, 0, 0.08);
  }

  .dog-fact h4 {
    margin: 0 0 0.8rem 0;
    font-size: 1.2rem;
    color: #FE6A37;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .dog-fact p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.9);
  }

  :global(.light) .dog-fact p {
    color: rgba(0, 0, 0, 0.9);
  }
  
  .fact-text {
    animation: fadeInText 1s ease;
    position: relative;
    padding: 0 1rem;
  }
  
  .fact-text::before,
  .fact-text::after {
    content: '"';
    position: absolute;
    font-size: 1.5rem;
    color: rgba(255, 255, 255, 0.3);
  }

  :global(.light) .fact-text::before,
  :global(.light) .fact-text::after {
    color: rgba(0, 0, 0, 0.3);
  }
  
  .fact-text::before {
    left: 0;
    top: -0.5rem;
  }
  
  .fact-text::after {
    right: 0;
    bottom: -0.5rem;
  }
  
  @keyframes fadeInText {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .dashboard-button {
    margin-top: 1.5rem;
    padding: 0.8rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
    color: white;
    background: linear-gradient(90deg, #FF9A3E, #FF6A00);
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(255, 154, 62, 0.3);
  }
  
  .dashboard-button:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(255, 154, 62, 0.4);
  }
  
  @media (max-width: 600px) {
    .loading-content {
      width: 90%;
      padding: 1.5rem;
    }
    
    h3 {
      font-size: 1.2rem;
    }
    
    .dog-fact h4 {
      font-size: 1rem;
    }
    
    .dog-fact p {
      font-size: 0.9rem;
    }
  }
</style>
